/**
 * EAA-1820: presentational cell primitives shared by both account tables. Each
 * context maps its OWN status vocabulary onto the generic `tone` here — realm:
 * enabled/pending-first-login/disabled; org: ok/pending/sync-error/revoked — the
 * mapping stays in each context's table (it is domain logic), only the rendered
 * dot+label widget is shared.
 */

import { initialsOf, type CellTone } from './cellHelpers';
import cells from '../accountsCells.module.css';

export type { CellTone } from './cellHelpers';

/**
 * EAA-1846: the tone class used to be built as `acc-status--${tone}`. A template
 * literal cannot survive CSS Modules — the hashed name is only knowable through
 * the imported map — so the three tones are an explicit lookup. It also makes an
 * unmapped tone a type error rather than a silently unstyled chip.
 */
const STATUS_TONE: Record<CellTone, string> = {
    ok: cells.statusOk,
    warn: cells.statusWarn,
    off: cells.statusOff,
};

export function StatusChip({ tone, label }: { tone: CellTone; label: string }) {
    return (
        <span className={`${cells.status} ${STATUS_TONE[tone]}`}>
            <span className={cells.statusDot} aria-hidden="true" />
            {label}
        </span>
    );
}

/**
 * "Tài khoản" column: avatar-initials + primary text, an OPTIONAL secondary line,
 * optionally a button.
 *
 * EAA-1839: `secondary` became optional, and the second line is now omitted rather
 * than rendered as an em dash when there is nothing to put in it. The org board
 * stopped showing the raw `user_sub` under the email (a 36-character UUID on every
 * row), and passing `secondary=""` to satisfy a required prop painted a dangling
 * "—" on every row instead — a placeholder standing in for a value that was
 * deliberately removed, which reads as missing data rather than as a choice.
 * `primary` keeps its `|| '—'` fallback: that line is the row's identity and an
 * empty one is genuinely a gap worth showing.
 */
export function IdentityCell({
    primary,
    secondary,
    onClick,
}: {
    primary: string;
    secondary?: string;
    onClick?: () => void;
}) {
    const content = (
        <>
            <span className={cells.avatar} aria-hidden="true">
                {initialsOf(primary || secondary || '')}
            </span>
            <span className={cells.identityText}>
                <span className={cells.username}>{primary || '—'}</span>
                {secondary && <span className={cells.email}>{secondary}</span>}
            </span>
        </>
    );
    if (!onClick) {
        return <span className={cells.identity}>{content}</span>;
    }
    return (
        <button type="button" className={cells.identity} onClick={onClick}>
            {content}
        </button>
    );
}
